import React, { useState, useEffect } from "react";
import { format, addHours, setHours, setMinutes, isWithinInterval, isValid, areIntervalsOverlapping, addDays, isSameDay, startOfWeek, endOfWeek } from "date-fns";
import { toLocalISOString, fromLocalISOString } from "@/utils/timezoneUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useGuards } from "@/pages/scheduling/hooks/useGuards";
import { CalendarEvent } from "@/pages/scheduling/types";
import { AlertCircle, Check, AlertTriangle, Search, Info } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { checkGuardScheduleConflicts } from "../services/totalWorkService";

interface AddScheduleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  teamId: string;
  onEventAdded: () => void;
  event?: CalendarEvent | null;
  createShift?: (shift: CalendarEvent) => Promise<CalendarEvent | null>;
  updateShift?: (shiftId: string, updates: Partial<CalendarEvent>) => Promise<CalendarEvent | null>;
}

interface VacationType {
  id: string;
  label: string;
  timeRange?: {
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
  };
}

const vacationTypes: VacationType[] = [
  {
    id: "previous-year-leave",
    label: "ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ",
    timeRange: { startHour: 14, startMinute: 1, endHour: 22, endMinute: 0 }
  },
  {
    id: "normal-leave",
    label: "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ",
    timeRange: { startHour: 14, startMinute: 1, endHour: 22, endMinute: 0 }
  },
  {
    id: "maternity-leave",
    label: "ΜΗ ΕΡΓΑΣΙΑ",
    timeRange: { startHour: 0, startMinute: 0, endHour: 0, endMinute: 1 }
  },
  {
    id: "sick-leave",
    label: "ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ",
    timeRange: { startHour: 14, startMinute: 1, endHour: 22, endMinute: 0 }
  },
  {
    id: "day-off",
    label: "ΡΕΠΟ",
    timeRange: { startHour: 13, startMinute: 0, endHour: 13, endMinute: 1 }
  }
];

const AddScheduleDialog: React.FC<AddScheduleDialogProps> = ({
  isOpen,
  onClose,
  selectedDate,
  teamId,
  onEventAdded,
  event,
  createShift: createShiftProp,
  updateShift: updateShiftProp
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { availableGuards: guards, allGuards, isLoading: isLoadingGuards } = useGuards(teamId || '');
  
  // Use provided shift operations or fallback to direct supabase calls
  const createShift = createShiftProp;
  const updateShift = updateShiftProp;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isGuardsDropdownOpen, setIsGuardsDropdownOpen] = useState(false);
  const [conflicts, setConflicts] = useState<{guardId: string, guardName: string}[]>([]);
  const [checkedGuards, setCheckedGuards] = useState<Record<string, boolean>>({});
  const [guardSearchText, setGuardSearchText] = useState("");
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  
  // Safely initialize dates with validation
  const getValidDate = (date: Date, defaultHour: number = 9) => {
    const baseDate = new Date(date);
    if (isNaN(baseDate.getTime())) {
      // Fallback to current date if invalid
      return setHours(setMinutes(new Date(), 0), defaultHour);
    }
    return setHours(setMinutes(baseDate, 0), defaultHour);
  };
  
  const [startDate, setStartDate] = useState<Date>(getValidDate(selectedDate, 9));
  const [endDate, setEndDate] = useState<Date>(getValidDate(selectedDate, 17));
  const [assignedGuards, setAssignedGuards] = useState<string[]>([]);
  const [color, setColor] = useState<string>("primary");
  const [selectedVacationType, setSelectedVacationType] = useState<string>("");
  
  
  const startIso = startDate && !isNaN(startDate.getTime()) ? startDate.toISOString() : "";
  const endIso = endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : "";

  const { data: allTeamSchedules } = useQuery({
    queryKey: ['all-schedules-for-conflict-check', teamId, startIso, endIso],
    queryFn: async () => {
      if (!startDate || !endDate) return [] as any[];
      const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(startDate, { weekStartsOn: 1 });
      const fetchStart = new Date(weekStart.getTime() - 36 * 60 * 60 * 1000); // include previous night for rest calc
      const fetchEnd = new Date(weekEnd.getTime() + 24 * 60 * 60 * 1000); // include next day

      let query = supabase
        .from('team_schedules')
        .select('id, start_date, end_date, assigned_guards, title, team_id')
        .or(`and(start_date.lte.${fetchEnd.toISOString()},end_date.gte.${fetchStart.toISOString()})`)
        .order('start_date', { ascending: true });

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching schedules for guard status:', error);
        throw error;
      }
      return data || [];
    },
    enabled: !!startDate && !!endDate,
  });
  
  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      
      // Validate dates from event
      const eventStartDate = fromLocalISOString(event.start_date);
      const eventEndDate = fromLocalISOString(event.end_date);
      
      if (!isNaN(eventStartDate.getTime())) {
        setStartDate(eventStartDate);
      }
      if (!isNaN(eventEndDate.getTime())) {
        setEndDate(eventEndDate);
      }
      
      setAssignedGuards(event.assigned_guards || []);
      setColor(event.color || "primary");
      setSelectedVacationType("");
      
      const checkedGuardsMap: Record<string, boolean> = {};
      if (event.assigned_guards) {
        event.assigned_guards.forEach(id => {
          checkedGuardsMap[id] = true;
        });
      }
      setCheckedGuards(checkedGuardsMap);
    } else {
      setTitle("");
      setDescription("");
      
      // Validate selectedDate before using it
      const baseDate = new Date(selectedDate);
      if (!isNaN(baseDate.getTime())) {
        setStartDate(setHours(setMinutes(baseDate, 0), 9));
        setEndDate(setHours(setMinutes(baseDate, 0), 17));
      } else {
        // Fallback to current date if selectedDate is invalid
        const fallbackDate = new Date();
        setStartDate(setHours(setMinutes(fallbackDate, 0), 9));
        setEndDate(setHours(setMinutes(fallbackDate, 0), 17));
      }
      
      setAssignedGuards([]);
      setColor("primary");
      setSelectedVacationType("");
    }
  }, [event, selectedDate]);
  
  useEffect(() => {
    const checkConflicts = async () => {
      if (!startDate || !endDate) return;
      
      const newConflicts: {guardId: string, guardName: string}[] = [];
      
      for (const guardId of assignedGuards) {
        const hasConflict = await checkGuardScheduleConflicts(
          guardId,
          startDate,
          endDate,
          event?.id,
          allTeamSchedules
        );
        
        if (hasConflict) {
          const guardName = guards?.find(g => g.id === guardId)?.full_name || guardId;
          newConflicts.push({guardId, guardName});
        }
      }
      
      setConflicts(newConflicts);
    };
    
    checkConflicts();
  }, [assignedGuards, startDate, endDate, event, guards, allTeamSchedules]);
  
  useEffect(() => {
    if (selectedVacationType) return;
    
    if (title.trim()) {
      if (!event?.id) {
        const titleLower = title.toLowerCase();
        setColor("primary");
      }
    }
  }, [title, event?.id, selectedVacationType]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      if (!user) throw new Error(t("userNotAuthenticated"));
      if (!title.trim()) throw new Error(t("titleRequired"));
      
      if (endDate <= startDate) {
        throw new Error(t("endDateMustBeAfterStartDate"));
      }
      
      for (const guardId of assignedGuards) {
        const hasConflict = await checkGuardScheduleConflicts(
          guardId,
          startDate,
          endDate,
          event?.id,
          allTeamSchedules
        );
        
        if (hasConflict) {
          const guardName = guards?.find(g => g.id === guardId)?.full_name || guardId;
          throw new Error(`${t("guardHasConflict") || "Guard has conflict"}: ${guardName}. ${t("minuteOverlapsNotAllowed") || "Even minute overlaps are not allowed."}`);
        }
      }
      
      if (conflicts.length > 0) {
        throw new Error(t("scheduleConflictsDetected") || "Schedule conflicts detected. Guards cannot have overlapping schedules, even by a minute.");
      }
      
      // Make sure assignedGuards is never null
      const guardsToAssign = assignedGuards || [];
      
      if (event?.id) {
        console.log('=== UPDATING EXISTING SHIFT ===');
        console.log('Event ID:', event.id);
        console.log('Team ID:', teamId);
        console.log('User ID:', user.id);
        
        // Use the updateShift function from useShiftApi
        const updateData = {
          title,
          description,
          start_date: toLocalISOString(startDate),
          end_date: toLocalISOString(endDate),
          assigned_guards: guardsToAssign,
          color,
          team_id: teamId
        };
        
        console.log('Update data:', updateData);
        
        if (!updateShift) {
          throw new Error("updateShift function not provided");
        }
        
        const updatedShift = await updateShift(event.id, updateData);
        
        if (!updatedShift) {
          throw new Error(t("errorUpdatingSchedule") || "Error updating schedule");
        }
        
        toast({
          title: t("success"),
          description: t("scheduleUpdated"),
        });
      } else {
        console.log('=== CREATING NEW SHIFT ===');
        console.log('Team ID:', teamId);
        console.log('User ID:', user.id);
        
        // Use the createShift function from useShiftApi
        const newShiftData: CalendarEvent = {
          id: crypto.randomUUID(),
          team_id: teamId,
          title,
          description,
          start_date: toLocalISOString(startDate),
          end_date: toLocalISOString(endDate),
          assigned_guards: guardsToAssign,
          color,
          created_by: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        console.log('=== SHIFT CREATION DEBUG ===');
        console.log('Title being used for shift:', title);
        console.log('Assigned guards:', guardsToAssign);
        console.log('Guards data:', guards);
        console.log('New shift data:', newShiftData);
        
        if (!createShift) {
          throw new Error("createShift function not provided");
        }
        
        const createdShift = await createShift(newShiftData);
        
        if (!createdShift) {
          throw new Error(t("errorCreatingSchedule") || "Error creating schedule");
        }
        
        toast({
          title: t("success"),
          description: t("scheduleCreated"),
        });
      }
      
      onEventAdded();
      onClose();
      
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      setError(error.message || t("errorSavingSchedule"));
      
      toast({
        variant: "destructive",
        title: t("error"),
        description: error.message || t("errorSavingSchedule"),
      });
    } finally {
      setLoading(false);
    }
  };
  
  const formatTimeOnly = (date: Date) => {
    if (!isValid(date)) {
      console.error("Invalid date provided to formatTimeOnly:", date);
      return "00:00";
    }
    return format(date, "HH:mm");
  };
  
  const formatDateForInput = (date: Date) => {
    if (!isValid(date)) {
      console.error("Invalid date provided to formatDateForInput:", date);
      return "";
    }
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };
  
  const generateTimeOptions = () => {
    const options = [];
    
    // Add XX:01 options for every hour (00:01, 01:01, 02:01, etc.)
    for (let hour = 0; hour < 24; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:01`;
      options.push(time);
    }
    
    // Add regular 15-minute interval options
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(time);
      }
    }
    
    // Add next day options (marked with *)
    for (let hour = 0; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `*${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push(time);
      }
    }
    
    // Add special times that might not be covered by the intervals above
    const specialTimes = ["13:01", "14:01", "23:59"];
    
    for (const specialTime of specialTimes) {
      if (!options.includes(specialTime)) {
        options.push(specialTime);
      }
    }
    
    // Remove duplicates and sort
    const uniqueOptions = [...new Set(options)];
    
    return uniqueOptions.sort((a, b) => {
      const aIsNextDay = a.startsWith('*');
      const bIsNextDay = b.startsWith('*');
      
      if (aIsNextDay && !bIsNextDay) return 1;
      if (!aIsNextDay && bIsNextDay) return -1;
      
      const aClean = a.replace('*', '');
      const bClean = b.replace('*', '');
      
      return aClean.localeCompare(bClean);
    });
  };
  
  const handleTimeChange = (timeStr: string, isStart = true) => {
    // time change triggered
    
    const isNextDay = timeStr.startsWith('*');
    const cleanTimeStr = isNextDay ? timeStr.substring(1) : timeStr;
    const [hours, minutes] = cleanTimeStr.split(':').map(Number);
    
    // Validate selectedDate before using it
    const date = new Date(selectedDate);
    if (isNaN(date.getTime())) {
      console.error("Invalid selectedDate in handleTimeChange:", selectedDate);
      return;
    }
    
    let newDate = setHours(setMinutes(date, minutes), hours);
    
    if (isNextDay) {
      newDate = addHours(newDate, 24);
    }
    
    if (!isStart && timeStr === "23:59" && startDate && isSameDay(date, startDate)) {
      const startTimeHour = startDate.getHours();
      const startTimeMinute = startDate.getMinutes();
      
      if (startTimeHour > hours || (startTimeHour === hours && startTimeMinute >= minutes)) {
        newDate = addDays(newDate, 1);
        // adding a day to end date because it would be before start date
      }
    }
    
    if (isStart) {
      // set startDate
      setStartDate(newDate);
      
      if (endDate && newDate >= endDate) {
        const newEndDate = addHours(newDate, 1);
        // auto-adjust endDate to be after startDate
        setEndDate(newEndDate);
      }
    } else {
      // set endDate
      setEndDate(newDate);
    }
  };
  
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() && 
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };
  
  const getSelectedTimeDisplay = (date: Date, referenceDate: Date) => {
    if (!isValid(date) || !isValid(referenceDate)) {
      console.error("Invalid date in getSelectedTimeDisplay", { date, referenceDate });
      return "00:00";
    }
    
    const isSameDay = date.getDate() === referenceDate.getDate() && 
                      date.getMonth() === referenceDate.getMonth() &&
                      date.getFullYear() === referenceDate.getFullYear();
    
    const formattedTime = format(date, "HH:mm");
    if (isSameDay) {
      return formattedTime;
    } else {
      return `*${formattedTime}`;
    }
  };
  
  const toggleGuardSelection = async (guardId: string) => {
    // Fast client-side block if we already know there's a conflict for this guard
    const localStatus = (guardStatusMap as Record<string, { conflict: boolean }>)[guardId];
    if (localStatus?.conflict) {
      const guardName = guards?.find(g => g.id === guardId)?.full_name || guardId;
      toast({
        variant: "destructive",
        title: t("scheduleConflict") || "Schedule Conflict",
        description: `${guardName} ${t("hasMinuteOverlappingSchedule") || "has an overlapping schedule (even if just for a few minutes)"}`,
      });
      return;
    }

    let willHaveConflict = false;

    if (!checkedGuards[guardId]) {
      willHaveConflict = await checkGuardScheduleConflicts(
        guardId,
        startDate,
        endDate,
        event?.id,
        allTeamSchedules
      );
    }

    if (willHaveConflict) {
      const guardName = guards?.find(g => g.id === guardId)?.full_name || guardId;
      toast({
        variant: "destructive",
        title: t("scheduleConflict") || "Schedule Conflict",
        description: `${guardName} ${t("hasMinuteOverlappingSchedule") || "has an overlapping schedule (even if just for a few minutes)"}`,
      });
      return;
    }

    setCheckedGuards(prev => ({
      ...prev,
      [guardId]: !prev[guardId]
    }));

    setAssignedGuards(prev => {
      if (prev.includes(guardId)) {
        return prev.filter(id => id !== guardId);
      } else {
        return [...prev, guardId];
      }
    });
  };
  
  const getSelectedGuardsText = () => {
    if (!guards || assignedGuards.length === 0) {
      return t("selectGuards") || "Select Personnel";
    }
    
    if (assignedGuards.length === 1) {
      const selectedGuard = guards.find(g => g.id === assignedGuards[0]);
      return selectedGuard ? selectedGuard.full_name : (t("selectGuards") || "Select Personnel");
    }
    
    return `${assignedGuards.length} ${t("guardsSelected") || "personnel selected"}`;
  };
  
  const handleVacationTypeChange = (typeId: string) => {
    console.log("Vacation type changed to:", typeId);
    
    // Validate selectedDate
    const baseDate = new Date(selectedDate);
    const validDate = isNaN(baseDate.getTime()) ? new Date() : baseDate;
    
    if (typeId === "regular") {
      setSelectedVacationType("");
      setTitle("");
      
      setStartDate(setHours(setMinutes(validDate, 0), 9));
      setEndDate(setHours(setMinutes(validDate, 0), 17));
      return;
    }
    
    const selectedType = vacationTypes.find(type => type.id === typeId);
    
    if (selectedType) {
      console.log("Selected type:", selectedType);
      setSelectedVacationType(typeId);
      setTitle(selectedType.label);
      
      if (selectedType.timeRange) {
        const { startHour, startMinute, endHour, endMinute } = selectedType.timeRange;
        console.log(`Setting time range: ${startHour}:${startMinute} - ${endHour}:${endMinute}`);
        
        // Ensure we're creating new Date objects based on the validDate
        const newStartDate = setHours(setMinutes(new Date(validDate), startMinute), startHour);
        const newEndDate = setHours(setMinutes(new Date(validDate), endMinute), endHour);
        
        console.log("New start date:", newStartDate);
        console.log("New end date:", newEndDate);
        
        // Explicitly set both dates
        setStartDate(newStartDate);
        setEndDate(newEndDate);
      } else {
        setStartDate(setHours(setMinutes(new Date(validDate), 0), 0));
        setEndDate(setHours(setMinutes(new Date(validDate), 59), 23));
      }
    }
  };
  
  // Filter guards based on search text
  const filteredGuards = guards?.filter(guard => {
    if (!guardSearchText.trim()) return true;
    const searchLower = guardSearchText.toLowerCase();
    return guard.full_name.toLowerCase().includes(searchLower) ||
           guard.first_name?.toLowerCase().includes(searchLower) ||
           guard.last_name?.toLowerCase().includes(searchLower);
  }) || [];
  
  const guardStatusMap = React.useMemo(() => {
    const map: Record<string, { conflict: boolean; weeklyHours: number; needsRestStar: boolean; hoursSinceLastShift: number | null }> = {};
    if (!startDate || !endDate || !Array.isArray(allTeamSchedules)) {
      return map;
    }
    const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(startDate, { weekStartsOn: 1 });
    const targetInterval = { start: startDate, end: endDate };

    const normalizeAssigned = (ag: any): string[] => {
      if (Array.isArray(ag)) return ag as string[];
      if (typeof ag === 'string') {
        try {
          const parsed = JSON.parse(ag);
          if (Array.isArray(parsed)) return parsed as string[];
        } catch {}
        return ag.replace(/[{}\[\]"]+/g, '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    };

    // Build an index of events per guard, only for relevant window
    const guardIndex: Record<string, { start: Date; end: Date; id: string }[]> = {};
    for (const ev of allTeamSchedules as any[]) {
      const assigned = normalizeAssigned(ev.assigned_guards);
      if (!assigned.length) continue;
      const evStart = fromLocalISOString(ev.start_date);
      const evEnd = fromLocalISOString(ev.end_date);
      if (isNaN(evStart.getTime()) || isNaN(evEnd.getTime())) continue;
      // Consider events that overlap the target week or end before the start (for rest calc)
      const overlapsWeek = evEnd > weekStart && evStart < weekEnd;
      const endsBeforeStart = evEnd <= startDate;
      if (!overlapsWeek && !endsBeforeStart) continue;
      for (const gid of assigned) {
        if (event?.id && ev.id === event.id) continue; // exclude self when editing
        (guardIndex[gid] ||= []).push({ start: evStart, end: evEnd, id: ev.id });
      }
    }

    for (const g of guards || []) {
      const eventsForGuard = guardIndex[g.id] || [];
      let conflict = false;
      let weeklyHours = 0;
      let lastEndBeforeStart: Date | null = null;

      for (const ev of eventsForGuard) {
        if (!conflict && areIntervalsOverlapping({ start: ev.start, end: ev.end }, targetInterval)) {
          conflict = true;
        }
        const overlapStart = ev.start < weekStart ? weekStart : ev.start;
        const overlapEnd = ev.end > weekEnd ? weekEnd : ev.end;
        if (overlapEnd > overlapStart) {
          weeklyHours += (overlapEnd.getTime() - overlapStart.getTime()) / 3600000;
        }
        if (ev.end <= startDate) {
          if (!lastEndBeforeStart || ev.end > lastEndBeforeStart) {
            lastEndBeforeStart = ev.end;
          }
        }
      }

      const hoursSinceLastShift = lastEndBeforeStart ? (startDate.getTime() - lastEndBeforeStart.getTime()) / 3600000 : null;
      const needsRestStar = hoursSinceLastShift !== null ? hoursSinceLastShift < 11 : false;
      map[g.id] = { conflict, weeklyHours, needsRestStar, hoursSinceLastShift };
    }
    return map;
  }, [guards, allTeamSchedules, startDate, endDate, event?.id]);

  
  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {event?.id ? t("editSchedule") : "Create Shift"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-destructive/10 p-3 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
          
          {conflicts.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("scheduleConflict") || "Schedule Conflict"}</AlertTitle>
              <AlertDescription>
                <div className="text-sm mt-1">
                  {t("theFollowingGuardsHaveConflicts") || "The following guards have scheduling conflicts:"}
                  <ul className="list-disc pl-5 mt-1">
                    {conflicts.map(conflict => (
                      <li key={conflict.guardId}>{conflict.guardName}</li>
                    ))}
                  </ul>
                  <p className="mt-2 font-semibold">{t("guardOverlapNotAllowed") || "Guards cannot have overlapping schedules."}</p>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid gap-2">
            <Label htmlFor="vacationType">Τύπος Άδειας</Label>
            <Select
              value={selectedVacationType || "regular"}
              onValueChange={handleVacationTypeChange}
            >
              <SelectTrigger id="vacationType">
                <SelectValue placeholder="Επιλέξτε τύπο άδειας" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Κανονική βάρδια</SelectItem>
                {vacationTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="title">Post*</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("enterTitle") || "Enter title"}
              required
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">{t("description") || "Description"}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("enterDescription") || "Enter description"}
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-time">{t("startTime") || "Start Time"}*</Label>
              <Select 
                value={isValid(startDate) ? getSelectedTimeDisplay(startDate, selectedDate) : "00:00"}
                onValueChange={(value) => handleTimeChange(value, true)}
              >
                <SelectTrigger id="start-time">
                  <SelectValue placeholder={isValid(startDate) ? getSelectedTimeDisplay(startDate, selectedDate) : "00:00"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {generateTimeOptions().map(time => (
                    <SelectItem key={`start-${time}`} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <input 
                type="hidden" 
                value={isValid(startDate) ? formatDateForInput(startDate) : ""} 
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="end-time">{t("endTime") || "End Time"}*</Label>
              <Select 
                value={isValid(endDate) ? getSelectedTimeDisplay(endDate, selectedDate) : "00:00"}
                onValueChange={(value) => handleTimeChange(value, false)}
              >
                <SelectTrigger id="end-time">
                  <SelectValue placeholder={isValid(endDate) ? getSelectedTimeDisplay(endDate, selectedDate) : "00:00"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {generateTimeOptions().map(time => (
                    <SelectItem key={`end-${time}`} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <input 
                type="hidden" 
                value={isValid(endDate) ? formatDateForInput(endDate) : ""} 
              />
            </div>
          </div>
          
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="guards">{t("assignGuards") || "Assign Guards"}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("colorLegendInfo") || "Color legend info"}
                    className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">{t("colorLegend") || "Τι σημαίνουν τα χρώματα"}</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Πράσινο: διαθέσιμος χωρίς σύγκρουση (δίπλα φαίνονται οι ώρες εβδομάδας με πράσινο)</li>
                      <li>Πορτοκαλί: κοντά/πάνω από το εβδομαδιαίο όριο ωρών (δίπλα φαίνονται οι ώρες εβδομάδας)</li>
                      <li>Κόκκινο: χρονική σύγκρουση με άλλη βάρδια (μη διαθέσιμος)</li>
                      <li>Γκρι: μη διαθέσιμος/ανενεργός</li>
                      <li>Αστεράκι (*): λιγότερες από 11 ώρες από την τελευταία βάρδια (δίπλα φαίνονται οι ώρες από τότε)</li>
                    </ul>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsGuardsDropdownOpen(!isGuardsDropdownOpen)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {getSelectedGuardsText()}
              </button>
              
              {isGuardsDropdownOpen && (
                <div className="absolute left-0 z-[80] mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
                  <div className="p-3 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={t("searchGuards") || "Search guards..."}
                        value={guardSearchText}
                        onChange={(e) => setGuardSearchText(e.target.value)}
                        className="pl-10"
                        autoFocus
                      />
                    </div>
                  </div>
                  <ScrollArea className="h-60 p-2">
                    {isLoadingGuards ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {t("loading") || "Loading"}...
                      </div>
                    ) : filteredGuards && filteredGuards.length > 0 ? (
                      <div className="space-y-2">
                        {filteredGuards.map(guard => {
                          const status = guardStatusMap[guard.id] || { conflict: false, weeklyHours: 0, needsRestStar: false, hoursSinceLastShift: null };
                          const hasConflict = status.conflict;
                          const weeklyHours = status.weeklyHours;
                          const dataReady = !!(startDate && endDate && Array.isArray(allTeamSchedules));
                          const colorClass = !dataReady
                            ? 'text-foreground'
                            : hasConflict
                              ? 'text-destructive'
                              : weeklyHours >= 40
                                ? 'text-amber-600'
                                : weeklyHours <= 32
                                  ? 'text-green-600'
                                  : 'text-foreground';
                          const name = `${status.needsRestStar ? '* ' : ''}${guard.full_name}`;
                          const isOrange = dataReady && !hasConflict && weeklyHours >= 40;
                          const isGreen = dataReady && !hasConflict && weeklyHours <= 32;
                          const showWeekly = isOrange || isGreen;
                          const showRest = dataReady && status.needsRestStar && status.hoursSinceLastShift != null;
                          const formatHours = (h: number) => Math.round(h * 10) / 10;
                          const metrics: string[] = [];
                          if (showWeekly) metrics.push(`${formatHours(weeklyHours)}h`);
                          if (showRest) metrics.push(`${formatHours(status.hoursSinceLastShift as number)}h`);
                          const metricsColorClass = isGreen ? 'text-green-600' : isOrange ? 'text-amber-600' : 'text-muted-foreground';
                          return (
                            <div key={guard.id} className={`flex items-center space-x-2 p-2 hover:bg-muted rounded-md ${hasConflict ? 'opacity-60 cursor-not-allowed' : ''}`}>
                              <Checkbox
                                id={`guard-${guard.id}`}
                                checked={checkedGuards[guard.id] || false}
                                onCheckedChange={() => toggleGuardSelection(guard.id)}
                                disabled={hasConflict}
                              />
                              <label
                                htmlFor={`guard-${guard.id}`}
                                className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer w-full ${colorClass}`}
                              >
                                {name}
                                {metrics.length > 0 && (
                                  <span className={`ml-1 ${metricsColorClass}`}>· {metrics.join(' · ')}</span>
                                )}
                                {hasConflict && (
                                  <span className="text-xs text-destructive block">
                                    {t("hasConflict") || "Has conflict"}
                                  </span>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {guardSearchText ? (t("noGuardsFound") || "No guards found") : (t("noGuardsAvailable") || "No guards available")}
                      </div>
                    )}
                  </ScrollArea>
                  <div className="p-2 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsGuardsDropdownOpen(false);
                        setGuardSearchText("");
                      }}
                      className="w-full"
                    >
                      {t("close") || "Close"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="color">{t("color") || "Color"}</Label>
            <Select
              value={color}
              onValueChange={(value) => setColor(value)}
            >
              <SelectTrigger id="color">
                <SelectValue placeholder={t("selectColor") || "Select color"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">{t("primary") || "Primary"}</SelectItem>
                <SelectItem value="secondary">{t("secondary") || "Secondary"}</SelectItem>
                <SelectItem value="destructive">{t("urgent") || "Urgent"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              type="submit" 
              disabled={loading || conflicts.length > 0}
            >
              {loading ? (t("saving") || "Saving...") : (event?.id ? (t("update") || "Update") : (t("save") || "Save"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddScheduleDialog;
